import { useState } from 'react';

interface SocialPostCardProps {
  label: string;
  content: string;
  icon: string;
  accentBg: string;
  accentText: string;
  index?: number;
}

export function SocialPostCard({
  label,
  content,
  icon,
  accentBg,
  accentText,
  index,
}: SocialPostCardProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group bg-white rounded-xl border border-stone-100 p-4 hover:border-orange-200 transition-all duration-200">
      <div className="flex items-start gap-3">
        {index !== undefined ? (
          <span className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-stone-100 text-stone-400 text-xs font-bold group-hover:bg-orange-500 group-hover:text-white transition-colors">
            {index + 1}
          </span>
        ) : (
          <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-md ${accentBg} mt-0.5`}>
            <i className={`${icon} ${accentText} text-xs`} />
          </div>
        )}
        <p className="flex-1 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{content}</p>
        <button
          onClick={handleCopy}
          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
            copied
              ? 'bg-green-500 border-green-500 text-white'
              : 'bg-stone-50 border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500'
          }`}
          title="Copy to clipboard"
        >
          <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
        </button>
      </div>
    </div>
  );
}

interface CopyAllButtonProps {
  items: string[];
  label: string;
}

export function CopyAllButton({ items, label }: CopyAllButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = items.map((item, i) => `${i + 1}. ${item}`).join('\n\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap ${
        copied
          ? 'bg-green-500 border-green-500 text-white'
          : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-orange-300 hover:text-orange-500'
      }`}
    >
      <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
      {copied ? 'Copied!' : `Copy All ${label}`}
    </button>
  );
}

interface HashtagChipProps {
  tag: string;
}

export function HashtagChip({ tag }: HashtagChipProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(tag);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
        copied
          ? 'bg-green-500 border-green-500 text-white'
          : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600'
      }`}
    >
      {copied ? <i className="ri-check-line text-xs" /> : null}
      {tag}
    </button>
  );
}
